import { addDays, isSaturday, isSunday } from 'date-fns';

import { PAYMENT_METHOD } from '@/types/payment-method';
import { PAYMENT_ACCOUNT_METHOD, PaymentPricing } from '@/types/payment-account';

/**
 * Feriados nacionais fixos (data.gov.br / calendário oficial). Carnaval e a
 * Sexta-feira Santa (móveis, via Páscoa) entram em {@link getNationalHolidays}.
 * Carnaval não é feriado nacional por lei, mas bancos e o repasse diário do
 * Stripe fecham nesses dois dias — por isso entram no cálculo de dias úteis.
 */
const FIXED_HOLIDAYS_MD: [month: number, day: number][] = [
  [1, 1], // Confraternização Universal
  [4, 21], // Tiradentes
  [5, 1], // Dia do Trabalho
  [9, 7], // Independência
  [10, 12], // Nossa Senhora Aparecida
  [11, 2], // Finados
  [11, 15], // Proclamação da República
  [11, 20], // Consciência Negra (nacional desde a Lei 14.759/2023)
  [12, 25], // Natal
];

/** Domingo de Páscoa do ano, pelo algoritmo de Meeus/Jones/Butcher (gregoriano). */
export function getEasterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = março, 4 = abril
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function dateKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

/** Feriados nacionais do ano (fixos + móveis via Páscoa), como chaves `dateKey`. */
export function getNationalHolidays(year: number): Set<string> {
  const holidays = new Set<string>();
  for (const [month, day] of FIXED_HOLIDAYS_MD) {
    holidays.add(dateKey(new Date(year, month - 1, day)));
  }
  // Também os feriados fixos do ano seguinte: uma simulação perto de 31/12
  // pode precisar de 1º de janeiro do ano seguinte.
  for (const [month, day] of FIXED_HOLIDAYS_MD) {
    holidays.add(dateKey(new Date(year + 1, month - 1, day)));
  }

  const easter = getEasterSunday(year);
  holidays.add(dateKey(addDays(easter, -47))); // Carnaval (segunda)
  holidays.add(dateKey(addDays(easter, -46))); // Carnaval (terça)
  holidays.add(dateKey(addDays(easter, -2))); // Sexta-feira Santa

  return holidays;
}

export function isBusinessDay(date: Date, holidays: Set<string>): boolean {
  if (isSaturday(date) || isSunday(date)) return false;
  return !holidays.has(dateKey(date));
}

/**
 * Soma `count` dias úteis a `date`, pulando fim de semana e feriado
 * nacional. `count = 0` devolve `date` caso já seja dia útil, senão o
 * próximo — usado para "confirmado hoje, disponível em 2 dias úteis" onde o
 * dia da confirmação não conta.
 */
export function addBusinessDays(
  date: Date,
  count: number,
  holidaysByYear = new Map<number, Set<string>>(),
): Date {
  const holidaysOf = (year: number) => {
    let set = holidaysByYear.get(year);
    if (!set) {
      set = getNationalHolidays(year);
      holidaysByYear.set(year, set);
    }
    return set;
  };

  let cursor = date;
  let remaining = count;
  while (remaining > 0) {
    cursor = addDays(cursor, 1);
    if (isBusinessDay(cursor, holidaysOf(cursor.getFullYear()))) {
      remaining -= 1;
    }
  }
  return cursor;
}

/** "1,19" → "1,19%". Só formatação — o número sempre vem do pricing. */
export function formatFeePercent(percent: number): string {
  return `${percent.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

export type SimulatableMethod = PAYMENT_ACCOUNT_METHOD;

export type PaymentSimulationRejectReason = 'BELOW_MIN' | 'ABOVE_MAX' | 'METHOD_UNAVAILABLE';

export interface PaymentSimulationInput {
  amount: number;
  method: SimulatableMethod;
  /** Data de referência do pagamento (confirmação). Padrão: agora. */
  date?: Date;
  pricing: PaymentPricing;
}

export interface PaymentSimulationResult {
  ok: boolean;
  reason?: PaymentSimulationRejectReason;
  method: SimulatableMethod;
  grossAmount: number;
  feePercent: number;
  feeFixed: number;
  /** Taxa do Stripe + margem da Controlapag (`marginPercent`, D1), já somadas. */
  feeAmount: number;
  netAmount: number;
  /** `null` quando `ok` é `false`: sem pagamento, não tem data de repasse. */
  availableOn: Date | null;
}

function feeForMethod(
  method: SimulatableMethod,
  pricing: PaymentPricing,
): { percent: number; fixed: number } {
  switch (method) {
    case PAYMENT_METHOD.PIX:
      return { percent: pricing.fee.pixPercent, fixed: 0 };
    case PAYMENT_METHOD.CREDIT_CARD:
      return { percent: pricing.fee.cardPercent, fixed: pricing.fee.cardFixed };
    case PAYMENT_METHOD.BANK_SLIP:
      return { percent: 0, fixed: pricing.fee.boletoFixed };
    default:
      return { percent: 0, fixed: 0 };
  }
}

function settlementBusinessDays(method: SimulatableMethod, pricing: PaymentPricing): number {
  return method === PAYMENT_METHOD.CREDIT_CARD
    ? pricing.settlement.cardDays
    : pricing.settlement.pixBusinessDays;
}

/**
 * Valor + forma + data (+ pricing) → taxa, líquido e data em que cai — a
 * função pura por trás do simulador de "Como funciona" (seção 6.2) e de
 * qualquer outra tela que precise mostrar o mesmo cálculo. Nunca embute
 * número de taxa/prazo/limite: tudo vem de `pricing` (T1.6).
 *
 * `cardDays` do settlement é sempre corrido no plano (30 dias corridos, seção
 * 2.3); só Pix/boleto usam dia útil. Ainda assim passamos pela mesma conta de
 * dias úteis para cartão com `cardDays` — como o valor típico (30) quase
 * nunca cai em cima de um feriado isolado, e a diferença de 1 dia não muda a
 * decisão do prestador, simplificamos para "dias corridos" no cartão e "dias
 * úteis" em Pix/boleto, como a fonte manda.
 */
export function simulatePayment({
  amount,
  method,
  date,
  pricing,
}: PaymentSimulationInput): PaymentSimulationResult {
  const reference = date ?? new Date();
  const { percent, fixed } = feeForMethod(method, pricing);

  const reject = (reason: PaymentSimulationRejectReason): PaymentSimulationResult => ({
    ok: false,
    reason,
    method,
    grossAmount: amount,
    feePercent: percent,
    feeFixed: fixed,
    feeAmount: 0,
    netAmount: 0,
    availableOn: null,
  });

  if (method === PAYMENT_METHOD.BANK_SLIP) {
    if (amount < pricing.boletoMin) return reject('BELOW_MIN');
    if (amount > pricing.boletoMax) return reject('ABOVE_MAX');
  }

  if (method === PAYMENT_METHOD.PIX) {
    if (amount < pricing.pixMin) return reject('BELOW_MIN');
    if (pricing.pixMax != null && amount > pricing.pixMax) return reject('ABOVE_MAX');
  }

  const effectivePercent = percent + pricing.marginPercent;
  const feeAmount = Math.round((amount * (effectivePercent / 100) + fixed) * 100) / 100;
  const netAmount = Math.round((amount - feeAmount) * 100) / 100;

  const availableOn =
    method === PAYMENT_METHOD.CREDIT_CARD
      ? addDays(reference, settlementBusinessDays(method, pricing))
      : addBusinessDays(reference, settlementBusinessDays(method, pricing));

  return {
    ok: true,
    method,
    grossAmount: amount,
    feePercent: effectivePercent,
    feeFixed: fixed,
    feeAmount,
    netAmount,
    availableOn,
  };
}

// ---------------------------------------------------------------------------
// Casos de exemplo (seção "Pronto quando" da T4.1) — documentação viva de
// como chamar `simulatePayment`, com os números que a T1.6 deve seedar
// (seção 2.2). Ficam só como comentário: um `EXAMPLE_*` executado ao carregar
// o módulo rodava `simulatePayment` com `new Date()` toda vez que qualquer
// tela importasse este arquivo (inclusive via `content/payments/shared.ts`),
// sem necessidade — o simulador de verdade roda sob demanda, com a data e o
// `pricing` de quem chama.
//
// const EXAMPLE_PRICING: PaymentPricing = {
//   enabled: true,
//   canActivate: true,
//   fee: { pixPercent: 1.19, cardPercent: 3.99, cardFixed: 0.39, boletoFixed: 3.45 },
//   marginPercent: 0,
//   pixMin: 0.5,
//   pixMax: null,
//   boletoMin: 5,
//   boletoMax: 49999.99,
//   cardBrands: ['visa', 'mastercard'],
//   settlement: { cardDays: 30, pixBusinessDays: 2 },
//   termsVersion: '2026-09',
// };
//
// R$ 100 no Pix: taxa 1,19% (R$ 1,19), líquido R$ 98,81, cai em 2 dias úteis.
// simulatePayment({ amount: 100, method: PAYMENT_METHOD.PIX, pricing: EXAMPLE_PRICING });
//
// R$ 100 no cartão: taxa 3,99% + R$ 0,39 (R$ 4,38), líquido R$ 95,62, cai em 30 dias.
// simulatePayment({ amount: 100, method: PAYMENT_METHOD.CREDIT_CARD, pricing: EXAMPLE_PRICING });
//
// R$ 4 no boleto: abaixo do mínimo de R$ 5 — `ok: false`, `reason: 'BELOW_MIN'`.
// simulatePayment({ amount: 4, method: PAYMENT_METHOD.BANK_SLIP, pricing: EXAMPLE_PRICING });
//
// Valor acima do limite do Pix (R$ 15.000 BR→BR, confirmado pelo Stripe na
// T0.1): `ok: false`, `reason: 'ABOVE_MAX'`.
// simulatePayment({ amount: 15500, method: PAYMENT_METHOD.PIX, pricing: { ...EXAMPLE_PRICING, pixMax: 15000 } });
